import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { isBoostedMultiple } from "@ppb/tbd-store/helpers/sportsbook-betting";
import { createSimpleSelectionsCounterSelector } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import {
  createGetCalculatedCombination,
  createGetGreatestOddCombinationSelector,
} from "../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { createSelector } from "reselect";
import { i18n } from "../../helpers/i18n";
import { DemonstrationCombination, createBuildDemonstrationCombination } from "../betslip-mapper";
import type { BetslipLocalization, BetslipOddsDisplay } from "../../contract";
import { BET_TYPES } from "@ppb/betslip-core";
import { currencyFormatWithDecimalPlaces } from "../../formatters/currency-adapter";
import { translateMultiple } from "../connected-sportsbook-betslip-mapper";
import { buildOdds } from "../betslip-formatters";

function buildCollapsedTitle(combination: DemonstrationCombination, context: string) {
  return i18n({
    key: "I18N.BETSLIP.COLLAPSED.TITLE",
    interpolationValues: {
      stake: combination.stake,
      context,
      odds: combination.odds,
      potentialReturns: combination.potentialReturns,
    },
  });
}

function buildNoOddsCollapsedTitle(combination: DemonstrationCombination, context: string) {
  return i18n({
    key: "I18N.BETSLIP.COLLAPSED.TITLE",
    interpolationValues: {
      context,
      odds: combination.odds,
    },
  });
}

function buildStartingPriceTitle(stake: number, betType: BET_TYPES, localization: BetslipLocalization): string {
  return i18n({
    key: "I18N.BETSLIP.COLLAPSED.TITLE",
    interpolationValues: {
      stake: currencyFormatWithDecimalPlaces({ ...localization, value: stake, decimalPlaces: 2 }),
      context: translateMultiple(betType),
      odds: i18n({ key: "I18N.BETSLIP.STARTING_PRICE" }),
      potentialReturns: i18n({ key: "I18N.BETSLIP.TBD" }),
    },
  });
}

export const createMinimizedTitleSelector = () => {
  const DEFAULT_STAKE = 10;
  const getCalculatedCombination = createGetCalculatedCombination();
  const buildDemonstrationCombination = createBuildDemonstrationCombination();
  const getCombination = createGetGreatestOddCombinationSelector();
  const getTotalSimpleSelections = createSimpleSelectionsCounterSelector();

  return createSelector(
    [
      (state: BetslipApplicationState) => state,
      (state: BetslipApplicationState) => getCombination(state),
      (state: BetslipApplicationState) => getTotalSimpleSelections(state),
      (_state: BetslipApplicationState, localization: BetslipLocalization) => localization,
      (_state: BetslipApplicationState, _localization: BetslipLocalization, oddsDisplay: BetslipOddsDisplay) =>
        oddsDisplay,
    ],
    (state, combination, totalSelections, localization, oddsDisplay): string | null => {
      // exclude boosted multiples because there were no product requirements
      if (!combination || isBoostedMultiple(combination)) return null;

      const calculatedCombination = getCalculatedCombination(state, {
        combinationId: combination.id,
        stake: DEFAULT_STAKE / combination.numLines,
      });

      if (!calculatedCombination) {
        return null;
      }

      const acca = buildDemonstrationCombination(state, {
        combination: calculatedCombination,
        localization,
        oddsDisplay,
      });

      if (!acca) {
        return null;
      }

      if (acca.betType !== BET_TYPES.SINGLE) {
        return buildCollapsedTitle(acca, acca.translatedBetType);
      }

      if (totalSelections !== 1) {
        return null;
      }

      return buildCollapsedTitle(acca, acca.translatedBetType);
    },
  );
};

export const createQuickBetslipTitleSelector = () => {
  const DEFAULT_STAKE = 10;
  const getCalculatedCombination = createGetCalculatedCombination();
  const buildDemonstrationCombination = createBuildDemonstrationCombination();

  return createSelector(
    [
      (state: BetslipApplicationState) => state,
      (state: BetslipApplicationState, combinationId: string) =>
        state.betting.sportsbookBetting.combinations[combinationId],
      (_state: BetslipApplicationState, _combinationId: string, localization: BetslipLocalization) => localization,
      (
        _state: BetslipApplicationState,
        _combinationId: string,
        _localization: BetslipLocalization,
        oddsDisplay: BetslipOddsDisplay,
      ) => oddsDisplay,
      (
        _state: BetslipApplicationState,
        _combinationId: string,
        _localization: BetslipLocalization,
        _oddsDisplay: BetslipOddsDisplay,
        fallbackStake?: number,
      ) => fallbackStake,
    ],
    (state, combination, localization, oddsDisplay, fallbackStake): string | null => {
      if (!combination) {
        return null;
      }

      const isSPSelection = combination.isSPAvailable && (combination.odds === null || combination.isSPSelected);

      // fallbackStake previews a stake that is about to be pre-filled, so the title does not change when it lands
      const titleStake = combination.stake || fallbackStake;
      const calculatedCombinationStake = titleStake ? titleStake : DEFAULT_STAKE;

      const calculatedCombination = getCalculatedCombination(state, {
        combinationId: combination.id,
        stake: calculatedCombinationStake / combination.numLines,
      });

      if (!calculatedCombination) {
        return null;
      }

      const acca = buildDemonstrationCombination(state, {
        combination: calculatedCombination,
        localization,
        oddsDisplay,
      });

      if (!acca) {
        if (isSPSelection && calculatedCombination.betType && calculatedCombination.totalStake) {
          return buildStartingPriceTitle(calculatedCombination.totalStake, calculatedCombination.betType, localization);
        }

        return null;
      }

      const odds =
        calculatedCombination.isPriceBoostSelected && calculatedCombination.priceBoostDisplayOdds
          ? buildOdds(calculatedCombination.priceBoostDisplayOdds, oddsDisplay)
          : acca.odds;

      if (!titleStake) {
        return buildNoOddsCollapsedTitle({ ...acca, odds }, acca.translatedBetType);
      }

      return buildCollapsedTitle({ ...acca, odds }, acca.translatedBetType);
    },
  );
};
